import { ApiClient } from '../api-client';

import {
  formatB2BTenantConfigResponse,
  formatTenantDetailsResponse,
  formatTenantList,
} from './formatters';
import {
  IUseTenantListQueryPayload,
  ITenantConfigOptions,
  ITenantDetailsResponseDataRaw,
  ITenantEnvironmentConfigResponse,
} from './types';
import { fetchTenantListUrl, tenantConfigUrl } from './urls';

/**
 * This provides the API client methods for all tenant related services like.
 * Such fetching tenant config, tenant specific data etc.
 */
export class TenantClient extends ApiClient {
  private static classInstance?: TenantClient;

  baseURL = process.env.NEXT_PUBLIC_TENANT_CONFIG_BASE_URL;

  private constructor() {
    super({
      baseURL: process.env.NEXT_PUBLIC_TENANT_CONFIG_BASE_URL,
    });
  }

  /**
   * Applying the dreaded singleton pattern here to reuse the axios instance.
   */
  public static getClientInstance = () => {
    if (!this.classInstance) {
      this.classInstance = new TenantClient();
    }

    return this.classInstance;
  };

  /**
   * Fetches the tenant config for the provided tenantId.
   */
  public fetchTenantConfig = async (identifier: string) => {
    const response = await this.get<ITenantDetailsResponseDataRaw>(
      tenantConfigUrl(identifier),
      {
        baseURL: this.baseURL,
        requiresAuth: false,
      }
    );

    if (!response.success) throw response;

    return formatB2BTenantConfigResponse(response.data, identifier);
  };

  public fetchTenantList = async (payload?: IUseTenantListQueryPayload) => {
    const response = await this.get<ITenantEnvironmentConfigResponse[]>(
      fetchTenantListUrl(),
      {
        requiresAuth: false,
        baseURL: process.env.NEXT_PUBLIC_TENANT_CONFIG_BASE_URL,
      }
    );

    if (!response.success) throw response;
    const { data } = response;
    // TODO: To discuss this with @divyansh....
    const formattedResponse = formatTenantList({
      appName: payload?.appName,
      tenantConfig: data[0] as ITenantEnvironmentConfigResponse,
    });
    return formattedResponse;
  };

  public fetchTenantDetails = async ({
    identifier,
    logoUrl,
  }: ITenantConfigOptions) => {
    const response = await this.get<ITenantDetailsResponseDataRaw>(
      tenantConfigUrl(identifier),
      {
        requiresAuth: false,
        baseURL: this.baseURL,
      }
    );

    if (!response.success) throw response;

    return formatTenantDetailsResponse(response.data, identifier, logoUrl);
  };
}

/**
 * This creates a new instance of the class. is th base Axios API client Class
 * wrapper for tenant config related services.
 */
export const TENANT_CONFIG_API = TenantClient.getClientInstance();
